import { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiContext } from '../../App';
import { toast } from 'react-toastify';
import CreateProcessBizDev from './CreateProcessBizDev';
import ConfirmationDialog from '../utils/ConfirmationDialog';
import {
  Plus,
  Edit3,
  Trash2,
  Users,
  Calendar,
  Target,
  MoreVertical,
  Search,
  Rocket
} from 'lucide-react';
import Loading from '../utils/Loading';
import Breadcrumb from '../utils/Breadcrumb';

const ProcessBizDevList = () => {
  const API = useContext(ApiContext);
  const navigate = useNavigate();
  const [processes, setProcesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterActive, setFilterActive] = useState('all'); // 'all', 'active', 'inactive'
  const [openDropdown, setOpenDropdown] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [processToDelete, setProcessToDelete] = useState(null);

  const fetchProcesses = useCallback(async () => {
    setLoading(true);
    try {
      const response = await API.get('/api/process-bizdev');
      setProcesses(response.data);
    } catch (error) {
      console.error('Error fetching processes:', error);
      toast.error('Erreur lors du chargement des projets BizDev');
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(() => {
    fetchProcesses();
  }, [fetchProcesses]);

  const handleCreateProcess = (newProcess) => {
    setProcesses(prev => [newProcess, ...prev]);
    fetchProcesses(); // Refresh pour avoir les données complètes
  };

  const handleViewProcess = (processId) => {
    navigate(`/process/bizdev/${processId}`);
  };

  const handleDeleteClick = (processId) => {
    setProcessToDelete(processId);
    setShowDeleteConfirm(true);
    setOpenDropdown(null); // Fermer le dropdown
  };

  const handleDeleteProcess = async (processId) => {
    try {
      await API.delete(`/api/process-bizdev/${processId}`);
      setProcesses(prev => prev.filter(p => p._id !== processId));
      toast.success('Projet supprimé avec succès');
    } catch (error) {
      console.error('Error deleting process:', error);
      toast.error('Erreur lors de la suppression du projet');
    }
  };

  const confirmDelete = () => {
    if (processToDelete) {
      handleDeleteProcess(processToDelete);
      setProcessToDelete(null);
    }
  };

  const toggleProcessStatus = async (processId, currentStatus) => {
    try {
      await API.put(`/api/process-bizdev/${processId}`, {
        active: !currentStatus
      });
      setProcesses(prev => prev.map(p => 
        p._id === processId ? { ...p, active: !currentStatus } : p
      ));
      toast.success(`Projet ${!currentStatus ? 'activé' : 'désactivé'} avec succès`);
    } catch (error) {
      console.error('Error updating process status:', error);
      toast.error('Erreur lors de la mise à jour du statut');
    }
  };

  // Filtrage des projet
  const filteredProcesses = processes.filter(process => {
    const matchesSearch = process.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         process.description?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filterActive === 'all' || 
                         (filterActive === 'active' && process.active) ||
                         (filterActive === 'inactive' && !process.active);

    return matchesSearch && matchesFilter;
  });

  // Afficher le loading pendant le chargement
    if (loading) {
        return (
            <div className="min-h-screen bg-gray-100 ">
                <Loading message="Chargement..." fullHeight={true}  />
            </div>
        );
    }

  return (
    <div className="min-h-screen bg-gray-100 mx-auto p-4 sm:p-6 lg:p-10 pt-16">
      {/* Breadcrumb */}
      <div className='pt-10'>
          <Breadcrumb items={[
          { label: 'Dashboard', href: '/app', icon: 'bi-house' },
          { label: 'Prospection', href: null, icon: 'bi bi-rocket-takeoff-fill', isActive: true }
      ]} />
      </div>
      
      {/* Header */}
      <div className="bg-white rounded-lg border mb-4">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center">
                <Rocket className="mr-3" size={28} />
                Prospection
              </h1>
              <p className="text-gray-600 mt-1">
                Pilotez vos projets de développement commercial
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-4 py-2 rounded-lg border border-blue-200">
                <div className="flex items-center space-x-2 text-blue-700">
                  <Target size={16} />
                  <span className="text-sm font-medium">Tableau de bord centralisé</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className='space-y-6'>
        {/* Header avec actions */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="flex flex-1 items-center space-x-4">
          {/* Barre de recherche */}
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Rechercher un projet..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Filtre */}
          <select
            value={filterActive}
            onChange={(e) => setFilterActive(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">Tous</option>
            <option value="active">Actifs</option>
            <option value="inactive">Inactifs</option>
          </select>
        </div>

        {/* Bouton créer */}
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center space-x-2 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
        >
          <Plus size={20} />
          <span>Nouveau projet</span>
        </button>
      </div>

      
      {/* Liste des projet */}
      <div className="bg-white rounded-lg border ">

        {filteredProcesses.length === 0 ? (
          <div className="p-12 text-center">
            <Target className="mx-auto h-12 w-12 text-gray-400 mb-2" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {processes.length === 0 ? 'Aucun projet commercial' : 'Aucun projet trouvé'}
            </h3>
            {processes.length === 0 && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center space-x-2 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
              >
                <Plus size={20} />
                <span>Créer un projet commercial</span>
              </button>
            )}
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredProcesses.map((process) => (
              <div key={process._id} onClick={() => handleViewProcess(process._id )} className="p-6 hover:bg-gray-50 transition-colors cursor-pointer
">
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900 truncate mb-0 hover:text-indigo-700 transition-all">
                        {process.name}
                      </h3>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        process.active 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {process.active ? 'Actif' : 'Inactif'}
                      </span>
                    </div>
                    
                    {process.description && (
                      <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                        {process.description}
                      </p>
                    )}

                    <div className="flex items-center space-x-6 text-sm text-gray-500">
                      <span className="flex items-center space-x-1">
                        <Users size={14} />
                        <span>{process.prospects?.length || 0} prospects</span>
                      </span>
                      <span className="flex items-center space-x-1">
                        <Target size={14} />
                        <span>{process.steps?.filter(s => s.active).length || 0} étapes actives</span>
                      </span>
                      <span className="flex items-center space-x-1">
                        <Calendar size={14} />
                        <span>{new Date(process.createdAt).toLocaleDateString('fr-FR')}</span>
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2 ml-4">
                    <div className="relative group">
                      <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenDropdown(openDropdown === process._id ? null : process._id);
                          }}
                          className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
                        <MoreVertical size={16} />
                      </button>
                      
                      <div className={` absolute right-0 top-8 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-10 transition-all ${openDropdown === process._id ? 'opacity-100 visible' : 'opacity-0 invisile'} `}>
                        <button
                          onClick={(e) => {e.stopPropagation(); toggleProcessStatus(process._id, process.active);}}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
                        >
                          <Edit3 size={14} />
                          <span>{process.active ? 'Désactiver' : 'Activer'}</span>
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteClick(process._id);
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center space-x-2"
                        >
                          <Trash2 size={14} />
                          <span>Supprimer</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal de création */}
      <CreateProcessBizDev
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onProcessCreated={handleCreateProcess}
      />

      {/* Modal de confirmation de suppression */}
      <ConfirmationDialog
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setProcessToDelete(null);
        }}
        onConfirm={confirmDelete}
        title="Supprimer le projet"
        message="Êtes-vous sûr de vouloir supprimer ce projet ? Tous les prospects associés seront également supprimés. Cette action ne peut pas être annulée."
        confirmText="Supprimer"
        cancelText="Annuler"
        isDangerous={true}
      />
      </div>
      
    </div>
  );
};

export default ProcessBizDevList;